import { Alert, Typography, styled } from "@mui/material";

import { StarburstIcon } from "@citrine/atoms/icons";
import { Column, Row } from "@citrine/atoms/layout";

import { CATALYST_CONTENT_BACKGROUND_COLOR } from "./catalyst.sx";

interface MessageBubbleProps {
  isUser?: boolean;
}

export const MessageBubble = styled("div", {
  shouldForwardProp: (prop) => prop !== "isUser",
})<MessageBubbleProps>(({ isUser, theme }) => ({
  alignSelf: isUser ? "flex-end" : "flex-start",
  backgroundColor: isUser
    ? theme.palette.primary.main
    : theme.palette.grey[100],
  borderRadius: theme.spacing(1.5),
  color: isUser
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  maxWidth: "85%",
  padding: theme.spacing(1, 1.5),
  whiteSpace: isUser ? "pre-wrap" : "normal",

  // Tables use the full panel width (85% is hostile to wide data); very wide
  // tables still scroll horizontally.
  ...(!isUser && {
    "&:has(table)": {
      // Override the base 85% cap so wide tables can use the full panel width.
      maxWidth: "100%",
      width: "100%",
    },
  }),
}));

export const MessageList = styled(Column)(({ theme }) => ({
  background: CATALYST_CONTENT_BACKGROUND_COLOR,
  // Fill the panel between header and input and scroll internally, so the
  // scrollbar sits flush at the panel's right edge (minHeight: 0 lets a flex
  // child shrink below its content height so it actually scrolls).
  flex: 1,
  gap: theme.spacing(1.5),
  minHeight: 0,
  overflowY: "auto",
  padding: theme.spacing(2),
  scrollBehavior: "smooth",
}));

// Errors are system conditions, not the agent's content, so they render as
// standalone (standard) Alerts outside the message bubble — spanning the full
// panel width to read as a system-level notice rather than a chat turn.
export const ErrorAlert = styled(Alert)({
  alignSelf: "stretch",
});

// A runtime event (tool call, retrieval, step progress) is process, not
// content — a quiet status line that recedes beneath the prose rather than a
// boxed chip. The agent glyph ties it to the Omni/agent vocabulary.
export const EventLine = styled(Row)(({ theme }) => ({
  gap: theme.spacing(0.75),
  margin: theme.spacing(1, 0, 0.25),
}));

export const EventGlyph = styled(StarburstIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  flexShrink: 0,
  fontSize: theme.spacing(1.75),
}));

// While streaming, the agent glyph pulses — scaling out from its center while
// fading — to signal "working", in place of a blinking caret. Extends EventGlyph
// so the icon/color stay identical to the event line (one vocabulary).
export const StreamingIndicator = styled(EventGlyph)({
  animation: "pulse 1.5s ease-in-out infinite",
  transformOrigin: "center",
  verticalAlign: "text-bottom",

  "@keyframes pulse": {
    "0%, 100%": { opacity: 0.4, transform: "scale(0.6)" },
    "50%": { opacity: 1, transform: "scale(1)" },
  },

  // Hold the glyph steady for users who opt out of motion.
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
});

export const Text = styled(Typography)({ wordBreak: "break-word" });

export const WelcomeText = styled(Typography)(({ theme }) => ({
  alignSelf: "center",
  color: theme.palette.text.secondary,
  padding: theme.spacing(4, 2),
}));
